import React, { useId, useRef, useState } from 'react';
import { Tooltip } from '@mui/material';
import { Link } from 'react-router-dom';
import { advancedLineupRows, advancedNumber, advancedPositionAlternatives, advancedRoster } from '../../utils/advancedStatistics';
import { matchPlayerName } from '../../utils/matchRecord';

const displayPlayerId = id => String(id).replace(/^flashscore-/, '');

export default function TacticalSummary({ record, tactics, acta, t, format }) {
  const [selectedSystem, setSelectedSystem] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [view, setView] = useState('pitch');
  const detailId = useId();
  const heading = useRef(null);
  const slotButtons = useRef({});
  const systems = Object.entries(tactics?.sistemas || {}).sort((a, b) => b[1].partidos - a[1].partidos);
  const preferred = tactics?.alineacionMasRepetida?.sistema || tactics?.sistemaMasUtilizado;
  const system = [selectedSystem, preferred].find(id => systems.some(([key]) => key === id)) || systems[0]?.[0];
  const systemData = tactics?.sistemas?.[system];
  const lineup = { sistema: system, ...systemData?.onceMasRepetido };
  const rows = advancedLineupRows(lineup);
  const alternatives = advancedPositionAlternatives(systemData);
  const slots = rows.length ? lineup.jugadores.map((_, index) => String(index + 1)) : alternatives.map(item => item.slot);
  const slot = slots.includes(selectedSlot) ? selectedSlot : slots[0];
  const candidates = alternatives.find(item => item.slot === slot)?.players || [];
  const roster = advancedRoster(record, acta);
  const sample = advancedNumber(tactics?.partidosConSistema);
  const complete = advancedNumber(systemData?.partidosConOnce);
  const shownId = rows.length ? String(lineup.jugadores[Number(slot) - 1]) : null;
  const nameFor = id => {
    const player = roster.find(item => String(item.codJugador) === String(id));
    return matchPlayerName(player?.nombre) || `${t('tactics.unknown')} · ${displayPlayerId(id)}`;
  };
  const profile = id => `/jugador?${new URLSearchParams({ idJugador: id, idTemporada: record.idTemporada })}`;
  const selectSlot = value => {
    setSelectedSlot(value);
    const stacked = heading.current && window.getComputedStyle(heading.current.parentElement).position === 'static';
    if (window.matchMedia('(max-width: 760px)').matches || stacked) {
      heading.current?.focus({ preventScroll: true });
      heading.current?.scrollIntoView?.({ block: 'start' });
    }
  };
  const renderSlot = (value, pitch = false) => {
    const id = rows.length ? String(lineup.jugadores[Number(value) - 1]) : null;
    const players = alternatives.find(item => item.slot === value)?.players || [];
    const name = id === null ? t('tactics.noShownPlayer') : nameFor(id);
    return <div key={value} className={`as-slot${pitch ? ' as-slot-pitch' : ''}`}>
      <Tooltip describeChild enterDelay={250} title={<div className="as-slot-preview">
        <strong>{t('tactics.slotNumber', { slot: value })} · {system}</strong>
        <span>{players.length ? t('tactics.observedPlayers', { count: players.length }) : t('tactics.noSlotAlternatives')}</span>
        {players.length > 0 && <span>{t('tactics.mostUsed')}: {nameFor(players[0].id)}</span>}
        <span>{t('tactics.previewHint')}</span>
      </div>} slotProps={{ popper: { disablePortal: true }, tooltip: { sx: {
        bgcolor: 'var(--as-panel)', color: 'var(--as-ink)', border: '1px solid var(--as-line)',
        fontSize: 12, p: 1.5, maxWidth: 280,
      } } }}>
        <button type="button" className="as-slot-select" aria-pressed={slot === value}
          aria-controls={detailId} aria-label={t('tactics.inspectSlot', { slot: value, name })}
          ref={element => { slotButtons.current[value] = element; }} onClick={() => selectSlot(value)}>
          <span className="as-slot-number" aria-hidden="true">{value}</span>
          <strong>{name}</strong>
          <span className="as-slot-count">{players.length
            ? t('tactics.playerCountShort', { count: players.length }) : t('tactics.noFrequency')}</span>
        </button>
      </Tooltip>
      {id !== null && <Link className="as-slot-profile" to={profile(id)}
        aria-label={t('tactics.playerProfile', { name })}>{t('tactics.profile')}</Link>}
    </div>;
  };
  return <aside className="as-tactics" aria-label={t('tactics.title')}>
    <h3>{t('tactics.title')}</h3>
    {!systems.length ? <p className="as-note">{t('tactics.empty')}</p> : <>
      <p className="as-note">{sample === null ? t('coverageUnknown') : t('tactics.coverage', { count: sample })}</p>
      <div className="as-systems">{systems.map(([id, item]) => {
        const share = sample > 0 ? item.partidos / sample * 100 : null;
        return <button type="button" key={id} aria-pressed={id === system} onClick={() => {
          setSelectedSystem(id);
          setSelectedSlot(null);
        }}>
          <span><strong>{id}</strong><span>{t('matches', { count: item.partidos })}
            {share !== null && ` · ${format(share, 0)}%`}</span></span>
          <span className="as-system-track" aria-hidden="true"><span
            style={{ width: `${share >= 0 && share <= 100 ? share : 0}%` }} /></span>
        </button>;
      })}</div>
      <div className="as-lineup-workspace">
        <section className="as-lineup-board" aria-label={t('tactics.lineup')}>
          <header className="as-lineup-heading">
            <div><h3>{t('tactics.lineup')}</h3><p className="as-note">{rows.length
              ? `${t('tactics.repetitions', { count: lineup.veces })}${lineup.ultimaFecha ? ` · ${lineup.ultimaFecha}` : ''}`
              : t('tactics.noLineup')}</p></div>
            {rows.length > 0 && <div className="as-view-switch" role="group" aria-label={t('tactics.view')}>
              {['pitch', 'list'].map(value => <button key={value} type="button" aria-pressed={view === value}
                onClick={() => setView(value)}>{t(`tactics.${value}`)}</button>)}
            </div>}
          </header>
          {slots.length > 0 && <p className="as-note as-slot-instruction">{t('tactics.selectHint')}</p>}
          {view === 'pitch' && rows.length > 0
            ? <div className="as-pitch" role="group" aria-label={t('tactics.lineup')}>
              <svg className="as-pitch-markings" viewBox="0 0 100 120" preserveAspectRatio="none"
                aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth=".5">
                <rect x="1" y="1" width="98" height="118" />
                <path d="M1 60h98M22 1v18h56V1M37 1v7h26V1M22 119v-18h56v18M37 119v-7h26v7" />
                <circle cx="50" cy="60" r="13" />
                <circle cx="50" cy="60" r=".7" fill="currentColor" />
              </svg>
              {rows.map((row, index) => <div className="as-pitch-row" key={index}>{row.map(id =>
                renderSlot(String(lineup.jugadores.indexOf(id) + 1), true))}</div>)}
            </div>
            : <div className="as-slot-grid" role="group" aria-label={t('tactics.list')}>{slots.map(value => renderSlot(value))}</div>}
          {slots.length > 0 && <p className="as-note as-selection-legend">{t('tactics.selectionLegend')}</p>}
          <p className="as-note">{t('tactics.disclaimer')}</p>
        </section>
        <section className="as-slot-detail" id={detailId} aria-labelledby={`${detailId}-heading`}>
          <h3>{t('tactics.alternatives')}</h3>
          <h4 id={`${detailId}-heading`} ref={heading} tabIndex={-1} aria-live="polite">
            {slot ? `${t('tactics.slotNumber', { slot })} · ${system}` : system}</h4>
          <p className="as-slot-sample">{complete === null ? t('coverageUnknown')
            : t('tactics.completeLineups', { count: complete })}</p>
          {shownId !== null && <div className="as-shown-player"><span>{t('tactics.inShownLineup')}</span>
            <Link to={profile(shownId)}>{nameFor(shownId)}</Link></div>}
          {candidates.length ? <ol className="as-candidate-list" aria-label={t('tactics.rankedPlayers')}>
            {candidates.map(item => <li key={item.id}>
              <div className="as-candidate-top"><div>
                <Link to={profile(item.id)}>{nameFor(item.id)}</Link>
                <div className="as-candidate-badges">
                  {item.count === candidates[0].count && <span>{t('tactics.mostUsed')}</span>}
                  {item.id === shownId && <span>{t('tactics.inShownLineup')}</span>}
                </div>
              </div><div className="as-candidate-frequency">
                <strong>{format(item.percentage)}{item.percentage !== null ? '%' : ''}</strong>
                <span>{item.percentage !== null ? `${format(item.count, 0)} / ${format(complete, 0)}`
                  : t('tactics.recordedTimes', { count: item.count })}</span>
              </div></div>
              {item.percentage !== null && <div className="as-candidate-track" aria-hidden="true">
                <span style={{ width: `${item.percentage}%` }} /></div>}
            </li>)}
          </ol> : <p className="as-empty">{t(slot ? 'tactics.noSlotAlternatives' : 'tactics.noAlternatives')}</p>}
          <p className="as-note">{t('tactics.frequencyMeaning')}</p>
          {slot && <button type="button" className="as-return-slots" onClick={() => {
            slotButtons.current[slot]?.focus({ preventScroll: true });
            slotButtons.current[slot]?.scrollIntoView?.({ block: 'center' });
          }}>{t('tactics.backToSlots')}</button>}
        </section>
      </div>
      <details className="as-tactics-method"><summary>{t('tactics.methodTitle')}</summary>
        <p className="as-note">{t('tactics.alternativesMethod')}</p>
      </details>
    </>}
  </aside>;
}
